import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Laptop, Moon, Save, Sun } from 'lucide-react';
import type { ThemePreference } from '../../shared/types';
import { roleAtLeast } from '../../shared/types';
import { PageHeader } from '../components/layout';
import { Alert, Avatar, Badge, Button, Card, Field, Input, TimezoneSelect, useToast } from '../components/ui';
import { api, ApiError } from '../lib/api';
import { qk } from '../lib/query';
import { useSessionStore } from '../stores/session';
import { useActiveWorkspace, useWorkspaceRole, useWorkspaceStore } from '../stores/workspace';
import { cn } from '../lib/utils';

const THEME_OPTIONS: Array<{ value: ThemePreference; label: string; icon: typeof Sun }> = [
  { value: 'light', label: 'Light', icon: Sun },
  { value: 'dark', label: 'Dark', icon: Moon },
  { value: 'system', label: 'System', icon: Laptop },
];

/* ================================================================== */
/* Profile — read only until the API exposes a profile-update endpoint */
/* ================================================================== */

function ProfileSection() {
  const user = useSessionStore((state) => state.user);
  if (!user) return null;

  return (
    <Card className="space-y-4 p-5">
      <div>
        <h2 className="text-sm font-semibold text-fg">Profile</h2>
        <p className="text-sm text-muted">How you appear across this deployment.</p>
      </div>
      <div className="flex items-center gap-4">
        <Avatar name={user.name} email={user.email} src={user.avatarUrl} size="lg" />
        <div className="min-w-0">
          <p className="truncate text-sm font-medium text-fg">{user.name ?? 'No name set'}</p>
          <p className="truncate text-sm text-muted">{user.email}</p>
        </div>
      </div>
      <Alert tone="info" title="Profile editing is not available yet">
        Your name and avatar currently come from Google sign-in (when used) or your invitation.
        Self-service editing lands in a future update.
      </Alert>
    </Card>
  );
}

/* ================================================================== */
/* Appearance                                                          */
/* ================================================================== */

function AppearanceSection() {
  const theme = useSessionStore((state) => state.theme);
  const setTheme = useSessionStore((state) => state.setTheme);

  return (
    <Card className="space-y-4 p-5">
      <div>
        <h2 className="text-sm font-semibold text-fg">Appearance</h2>
        <p className="text-sm text-muted">Dark is the default — light is fully supported too.</p>
      </div>
      <div className="grid grid-cols-3 gap-2">
        {THEME_OPTIONS.map((option) => {
          const Icon = option.icon;
          const active = theme === option.value;
          return (
            <button
              key={option.value}
              type="button"
              onClick={() => setTheme(option.value)}
              aria-pressed={active}
              className={cn(
                'flex flex-col items-center gap-2 rounded-lg border px-3 py-4 text-sm font-medium transition-colors',
                active
                  ? 'border-accent bg-accent-soft text-accent'
                  : 'border-border text-muted hover:border-border-strong hover:text-fg',
              )}
            >
              <Icon className="size-4" aria-hidden />
              {option.label}
            </button>
          );
        })}
      </div>
    </Card>
  );
}

/* ================================================================== */
/* Workspace                                                           */
/* ================================================================== */

function WorkspaceSection() {
  const workspaceId = useWorkspaceStore((state) => state.workspaceId);
  const workspace = useActiveWorkspace();
  const role = useWorkspaceRole();
  const canEdit = role ? roleAtLeast(role, 'admin') : false;
  const refresh = useSessionStore((state) => state.refresh);
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [name, setName] = useState('');
  const [timezone, setTimezone] = useState('America/Chicago');

  useEffect(() => {
    if (workspace) {
      setName(workspace.name);
      setTimezone(workspace.timezone);
    }
  }, [workspace?.id, workspace?.name, workspace?.timezone]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = useMutation({
    mutationFn: () => api.workspaces.update(workspaceId as string, { name: name.trim(), timezone }),
    onSuccess: async () => {
      void queryClient.invalidateQueries({ queryKey: qk.workspace(workspaceId) });
      await refresh();
      toast({ title: 'Workspace updated', tone: 'success' });
    },
    onError: (error) => {
      toast({
        title: 'Could not save workspace settings',
        description: error instanceof ApiError ? error.message : undefined,
        tone: 'error',
      });
    },
  });

  if (!workspace) return null;

  const dirty = name.trim() !== workspace.name || timezone !== workspace.timezone;

  return (
    <Card className="space-y-4 p-5">
      <div className="flex items-center justify-between gap-2">
        <div>
          <h2 className="text-sm font-semibold text-fg">Workspace</h2>
          <p className="text-sm text-muted">Name and timezone — schedules and “publish at 9am” use this zone.</p>
        </div>
        <Badge tone="neutral" className="capitalize">
          {role ?? '—'}
        </Badge>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Workspace name" htmlFor="settings-ws-name">
          <Input
            id="settings-ws-name"
            value={name}
            disabled={!canEdit}
            onChange={(event) => setName(event.target.value)}
          />
        </Field>
        <Field label="Timezone" htmlFor="settings-ws-timezone">
          <TimezoneSelect id="settings-ws-timezone" value={timezone} onChange={setTimezone} disabled={!canEdit} />
        </Field>
      </div>
      {canEdit ? (
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            leftIcon={<Save className="size-3.5" />}
            disabled={!dirty || !name.trim()}
            loading={save.isPending}
            onClick={() => save.mutate()}
          >
            Save changes
          </Button>
          {dirty && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setName(workspace.name);
                setTimezone(workspace.timezone);
              }}
            >
              Discard
            </Button>
          )}
        </div>
      ) : (
        <p className="text-xs text-subtle">Only owners and admins can change workspace settings.</p>
      )}
    </Card>
  );
}

/* ================================================================== */
/* Danger zone                                                         */
/* ================================================================== */

/**
 * There is no delete-workspace endpoint yet, so this section says so plainly
 * rather than dressing up a button that does nothing. A confirm-to-enable field
 * in front of a dead handler is worse than no button at all: it teaches people
 * the control works right up until the click that proves it does not.
 */
function DangerZoneSection() {
  const workspace = useActiveWorkspace();
  const role = useWorkspaceRole();

  if (!workspace || role !== 'owner') return null;

  return (
    <Card className="space-y-3 border-danger/30 p-5">
      <div>
        <h2 className="flex items-center gap-2 text-sm font-semibold text-danger">
          <AlertTriangle className="size-4" aria-hidden />
          Danger zone
        </h2>
        <p className="mt-1 text-sm text-muted">
          Deleting a workspace would remove its posts, connections, media and members — so it is not
          self-service yet.
        </p>
      </div>
      <Alert tone="warning" title="Workspace deletion is not available in this release">
        To retire <span className="font-medium text-fg">{workspace.name}</span>, remove its members
        from the Team page and disconnect its accounts. A superadmin can delete the record for you.
      </Alert>
    </Card>
  );
}

/* ================================================================== */
/* Page                                                                 */
/* ================================================================== */

/**
 * Settings — owned by WEB-4. Profile, appearance, workspace and danger zone.
 */
export function SettingsPage() {
  const workspaceId = useWorkspaceStore((state) => state.workspaceId);
  if (!workspaceId) return null;

  return (
    <>
      <PageHeader title="Settings" description="Your profile, appearance, and workspace preferences." />
      <div className="max-w-2xl space-y-6">
        <ProfileSection />
        <AppearanceSection />
        <WorkspaceSection />
        <DangerZoneSection />
      </div>
    </>
  );
}

export default SettingsPage;
